import React from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { resolveProfileAvatarUrl } from '../../lib/profileAvatar.js';
import Icon from './Icon.js';
import PasswordInput from './PasswordInput.jsx';

export function DCard({ children, style = {}, ...rest }) {
  return (
    <div style={{ background: C.darkMid, borderRadius: 14, border: '0.5px solid rgba(255,255,255,0.08)', ...style }} {...rest}>
      {children}
    </div>
  );
}

export function BtnGold({ children, onClick, style = {}, disabled = false, type = 'button' }) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={{ background: C.gold, color: '#0a0a0f', border: 'none', borderRadius: 12, padding: '12px 20px', fontWeight: 600, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', width: '100%', ...style }}
    >
      {children}
    </button>
  );
}

export function BtnGhost({ children, onClick, style = {} }) {
  return (
    <button onClick={onClick} style={{ background: 'transparent', color: 'rgba(255,255,255,0.5)', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 12, padding: '10px 20px', fontWeight: 500, fontSize: 12, cursor: 'pointer', width: '100%', ...style }}>
      {children}
    </button>
  );
}

export function SLabel({ children }) {
  return (
    <div style={{ padding: '12px 0 6px', fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 500 }}>
      {children}
    </div>
  );
}

export function SettingsRow({ icon, iconBg, iconColor, title, sub, right, onClick }) {
  return (
    <div className="settings-item" onClick={onClick} style={{ display: 'flex', alignItems: 'center', padding: '13px 0', gap: 12, cursor: onClick ? 'pointer' : 'default', borderBottom: '0.5px solid rgba(255,255,255,0.05)' }}>
      <div style={{ width: 32, height: 32, borderRadius: 9, background: iconBg || 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={icon} size={15} color={iconColor || 'rgba(255,255,255,0.5)'}/>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, color: '#fff' }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{sub}</div>}
      </div>
      {right || <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 16 }}>›</span>}
    </div>
  );
}

export function Toggle({ checked, onChange }) {
  return (
    <div className="toggle-wrap">
      <input type="checkbox" checked={checked} onChange={onChange}/>
      <div className="toggle-track"/>
      <div className="toggle-thumb"/>
    </div>
  );
}

export function FieldInput({ label, type, value, onChange, ...props }) {
  if (type === 'password') {
    return (
      <div style={{ marginBottom: 12 }}>
        {label && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>{label}</div>}
        <PasswordInput variant="dark" value={value} onChange={onChange} {...props} />
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>{label}</div>}
      <input style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '0.5px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
        onFocus={e => { e.target.style.borderColor = C.gold; }} onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }} type={type} value={value} onChange={onChange} {...props}/>
    </div>
  );
}

export function FieldTextarea({ label, rows = 4, ...props }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>{label}</div>}
      <textarea
        rows={rows}
        style={{ width: '100%', minHeight: 88, padding: '10px 12px', borderRadius: 10, border: '0.5px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'inherit', resize: 'vertical' }}
        onFocus={e => { e.target.style.borderColor = C.gold; }}
        onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
        {...props}
      />
    </div>
  );
}

export function Avatar({ initials = '?', bg = '#534AB7', color = '#EEEDFE', size = 36, imageUrl = null }) {
  const resolvedUrl = resolveProfileAvatarUrl(imageUrl);
  const hasImage = !!resolvedUrl;
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.33, fontWeight: 500, color, flexShrink: 0, overflow: 'hidden' }}>
      {hasImage ? (
        <img src={resolvedUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        initials
      )}
    </div>
  );
}

export function Badge({ children, type = 'gold', style: styleProp = {} }) {
  const styles = {
    gold:   { background: 'rgba(200,168,75,0.15)',   color: C.gold   },
    green:  { background: 'rgba(29,158,117,0.15)',   color: C.green  },
    purple: { background: 'rgba(83,74,183,0.15)',    color: C.purpleL },
    warn:   { background: 'rgba(239,159,39,0.15)',   color: C.warn   },
    danger: { background: 'rgba(226,75,74,0.1)',     color: C.danger },
    gray:   { background: 'rgba(255,255,255,0.06)',  color: 'rgba(255,255,255,0.4)' },
  };
  return (
    <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, fontWeight: 500, ...styles[type], ...styleProp }}>
      {children}
    </span>
  );
}
